import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Box } from 'theme-ui'
import { Nav } from 'components'
import { useAuth, useConfigUtils } from 'providers'
import { useCreate } from 'hooks/userHooks'
import { StudentFormContentLoader } from 'components/UserForm/StudentFormContentLoader'
import { TeacherCreateFormValues } from 'types'
import getLocalized from 'i18n'
import { CreateTeacherForm } from 'components/UserForm/CreateTeacherForm'
import { TEACHER, TEACHER_CREATED } from '../constants'
import * as access from 'types/access'

const localized = getLocalized()

export function CreateTeacher() {
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])

  const { profile, logout, hasAccess } = useAuth()
  const history = useHistory()
  const utils = useConfigUtils()
  const [state, create] = useCreate()
  const isCustomerSupportUser = profile.groups.includes('SupportGroup')

  async function handleLogoutClick() {
    await logout(profile.email)
    history.push({
      pathname: '/',
      state: undefined,
    })
  }
  const navigateToDashboard = useCallback(() => {
    history.push({ pathname: '/', state: TEACHER_CREATED })
  }, [history])

  const hasNavigated = useRef<boolean>(false)
  useEffect(() => {
    if (state.matches('success') && !hasNavigated.current) {
      hasNavigated.current = true
      navigateToDashboard()
    }
  }, [state, navigateToDashboard])

  function handleFormSubmit(values: TeacherCreateFormValues) {
    const formData = {
      ...values,
    }
    formData.userType = TEACHER
    create(formData)
  }

  const canCreateStudents =
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_TEST_USERS) ||
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_USERS)

  const canCreateTeachers = profile?.groups?.includes('SupportGroup')
  const canViewHelpCenter = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.VIEW_HELP_CENTER
  )

  return (
    <Box
      data-cy="create-teacher-page"
      sx={{
        bg: 'whitesmoke',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Nav
        profile={profile}
        onLogoutClick={handleLogoutClick}
        access={{
          createStudent: canCreateStudents,
          createTeacher: canCreateTeachers,
          viewHelpCenter: canViewHelpCenter,
        }}
      />
      <Box
        data-cy="teacher-create"
        sx={{
          width: '100vw',
          flexDirection: 'column',
          flex: '1 0 auto',
          alignItems: 'center',
          bg: 'white',
          pt: 68,
          position: 'relative',
        }}
      >
        {utils ? (
          <CreateTeacherForm
            pending={state.matches('syncing')}
            disabled={state.matches('syncing')}
            configUtils={utils}
            responseError={
              state.matches('failed') ? state.context.error : undefined
            }
            isSupport={isCustomerSupportUser}
            onFormSubmit={handleFormSubmit}
            onFormCancel={navigateToDashboard}
          />
        ) : (
          <Box variant="userProfile.form">
            <StudentFormContentLoader />
          </Box>
        )}
      </Box>
    </Box>
  )
}
